import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import ListItem from './ListItem';
import cssStyles from '../../../css/css-variables.json';
import { useViewport } from '../hooks/viewport';

const sharedStyled = css`
  padding: 0.833em 1.667em;

  li li & {
    padding: 0.357em 3.214em;
  }
  ${({ isActiveLink }) => isActiveLink &&
    `
      background-color: ${cssStyles.colors.Background};
      margin-left: -2px;
      text-decoration: none;
      color: ${cssStyles.colors.ActiveMenuItem};
    `
  }
  ${({ firstChild }) => firstChild && 'padding-top: 0.625em;'}
  ${({ lastChild }) => lastChild &&
    `
      border-bottom: 2px solid ${cssStyles.colors.AccentGray};
      border-top-width: 0;
    `
  }

  @media(max-width: 720px) {
    padding: 0;

    li li & {
      padding: 0 1.071em;
    }
  }
`;

const StyledLink = styled(({ isActiveLink, onlyChild, firstChild, lastChild, ...props }) => <Link {...props} />)`${sharedStyled}`;
StyledLink.displayName = 'StyledLink';

const StyledBtn = styled.button`
  ${sharedStyled}
  border: none;
  background-color: transparent;
  color: #16395f;
  ${({ isActiveButton, showChildren }) => (!showChildren && !isActiveButton) && `
      text-decoration: underline;
      color: #28599b;
  `}
`;
StyledBtn.displayName = 'StyledBtn';

let _clicked = false;

const MenuItem = props => {
  const { title, href, btnClick, children, isActive, location, isOpen, navToggle, icons, lastChild, firstChild, showChildren } = props;
  const { isMobile } = useViewport();
  const anchor = {
    onClick: (isOpen && isMobile && href) ? navToggle : null
  };

  let icon = (isActive || showChildren) ? icons.activeIcon || null : icons.inactiveIcon || null;
  icon = (React.Children.count(children)) ? icon : null;
  const currentPath = (location && location.pathname) || '';
  const qaHook = props['data-qa-hook'] ? { 'data-qa-hook': props['data-qa-hook'] } : null;

  // remedies form fields stealing menu focus
  const focusCheck = (e) => {
    if (e.target !== document.activeElement) {
      if (_clicked) e.target.click();
    }
    _clicked = false;
  };

  // helps correct click-stealing by forms
  const prepareClick = () => {
    _clicked = true;
  };

  return (
    <ListItem firstChild={firstChild} isActive={isActive} isMenu>
      {btnClick ?
        <StyledBtn
          onClick={btnClick}
          isActiveButton={isActive}
          onMouseDown={prepareClick}
          onMouseUp={focusCheck}
          lastChild={lastChild}
          showChildren={showChildren}
          {...qaHook}
        >
          {title}{icon}
        </StyledBtn>
        :
        <StyledLink
          to={href || `${currentPath}#`}
          isActiveLink={isActive}
          onMouseDown={prepareClick}
          onMouseUp={focusCheck}
          lastChild={lastChild}
          {...anchor}
          {...qaHook}
        >
          {title}{icon}
        </StyledLink>
      }
      {children}
    </ListItem>
  );
};

const { bool, node, string, object, func, shape } = PropTypes;

MenuItem.propTypes = {
  title: string.isRequired,
  href: string,
  children: node,
  isActive: bool,
  isOpen: bool,
  navToggle: func,
  location: object.isRequired,
  icons: shape({
    iconActive: node,
    iconInactive: node
  })
};

export default MenuItem;
